import PublishIcon from '@mui/icons-material/Publish';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { EmptySection } from '../../../components/EmptySection';
import { Loader } from '../../../components/Loader/Loader';
import { BulkUploadSummary, getBulkDealDocsUploads } from '../../../services/queries/BulkDealDocsQueries';
import { useAsync } from '../../../util/hook-utils';
import { BulkUploadDetailsModal } from './BulkUploadDetailsModal';
import { BulkUploadErrorBoundary } from './BulkUploadErrorBoundary';
import { selectedBulkDealDocsActionState } from './bulkDealDocsState';
import { BulkDealDocsUploadsGrid } from './BulkDealDocsUploadsGrid';
import { BULK_UPLOAD_CONFIG } from './constants';
import { BulkUploadLogger } from './logging';
import { UploadBulkDealDocsModal } from './UploadBulkDealDocsModal';

export const invalidateBulkDealDocsData = atom(0);

export function BulkDealDocsUploadLoader() {
  const invalidate = useAtomValue(invalidateBulkDealDocsData);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(BULK_UPLOAD_CONFIG.DEFAULT_PAGE_SIZE);

  const fetchBulkUploads = useCallback(async () => {
    try {
      const result = await getBulkDealDocsUploads(page, pageSize);
      return result;
    } catch (err) {
      BulkUploadLogger.error('Failed to load bulk uploads', err, { page, pageSize });
      throw err;
    }
  }, [page, pageSize]);

  const { data, loading, error } = useAsync(fetchBulkUploads, {
    id: `${invalidate}-${page}`,
  });

  if (!data) {
    return (
      <Box width='100%' height='100%' m='1rem'>
        {loading ? <Loader /> : error ? <Alert severity='error'>Failed to load data</Alert> : null}
      </Box>
    );
  }

  return (
    <BulkUploadErrorBoundary>
      <Box width='100%'>
        <UploadBulkDealDocsModal />
        <BulkUploadDetailsModal />
        <BulkDealDocsUpload
          data={data.items}
          page={page}
          pageSize={pageSize}
          totalItems={data.pagination.total}
          onPageChange={setPage}
        />
      </Box>
    </BulkUploadErrorBoundary>
  );
}

interface IBulkDealDocsUploadProps {
  data: BulkUploadSummary[];
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

export function BulkDealDocsUpload({
  data,
  page,
  pageSize,
  totalItems,
  onPageChange,
}: IBulkDealDocsUploadProps) {
  const hasData = data.length > 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Typography color='text.primary' component='section' width={'100%'} p={'1rem 2rem'}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} mb={'1rem'}>
        <Typography variant={'h3'}>Bulk Deal Documents Upload</Typography>
        {hasData && <UploadButton />}
      </Stack>
      <Stack spacing={2}>
        {!hasData ? (
          <EmptySection
            title='No Bulk Uploads'
            message='Upload a ZIP file containing deal documents organized by Fund/Company/Round folder structure'
            actionComponent={<UploadButton />}
          />
        ) : (
          <>
            <BulkDealDocsUploadsGrid data={data} />
            {totalPages > 1 && (
              <Stack direction='row' justifyContent='space-between' alignItems='center' px={2}>
                <Typography variant='body2' color='text.secondary'>
                  Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalItems)} of{' '}
                  {totalItems} uploads
                </Typography>
                <Stack direction='row' spacing={1}>
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={page === 1}
                    onClick={() => onPageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                    Page {page} of {totalPages}
                  </Typography>
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(page + 1)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Typography>
  );
}

function UploadButton() {
  const setSelectedAction = useSetAtom(selectedBulkDealDocsActionState);
  return (
    <Button
      size='medium'
      variant='contained'
      color='secondary'
      startIcon={<PublishIcon />}
      onClick={() => setSelectedAction('upload')}
    >
      Upload Bulk Deal Documents
    </Button>
  );
}
